import {get} from "lodash";
import {getBoolean, getInteger, getString} from "Core/helpers/data";
import {SORT_ORDER} from "Core/const/global";

/**
 * Unique Redux store key associated to this reducer
 * IMPORTANT: All reducers must export this value!
 * @type {string}
 */
export const reducerStoreKey = 'manageDocuments';

// Define reducer types handled by this reducers
export const REDUCER_TYPES = {
	RESET: '@manageDocuments/reset',
	SET_LIST_DATA: '@manageDocuments/set_list_data',
	CLEAR_LIST_DATA: '@manageDocuments/clear_list_data',
	SET_ITEM: '@manageDocuments/set_item',
	CLEAR_ITEM: '@manageDocuments/clear_item',
};

// Define action creators for all reducer types
export const actionCreators = {
	reset: () => ({type: REDUCER_TYPES.RESET}),
	/**
	 * @param {IoJsonFetchResponseObject} manageDocumentsListData
	 * @return {{type: string, listData: IoJsonFetchResponseObject}}
	 */
	
	setManageDocumentsListData: manageDocumentsListData => ({
		type: REDUCER_TYPES.SET_LIST_DATA, 
		listData: manageDocumentsListData
	}),
	clearManageDocumentsListData: () => ({type: REDUCER_TYPES.CLEAR_LIST_DATA}),
	
	/**
	 * @param {ManageDocumentsItemDataObject} manageDocumentsItem
	 * @return {{type: string, item: ManageDocumentsItemDataObject}}
	 */
	setManageDocumentsItem: manageDocumentsItem => ({
		type: REDUCER_TYPES.SET_ITEM, 
		item: manageDocumentsItem
	}),
	clearManageDocumentsItem: () => ({type: REDUCER_TYPES.CLEAR_ITEM}),
};

/**
 * Initial reducer state
 * IMPORTANT: All reducers must export initial state object!
 * @type {Object<string, any>}
 */
export const initialState = {
	/** @type {IoJsonFetchResponseObject} */
	listData: null,

	/**
	 * @note Value is null to differentiate between unloaded and new items (new items should be a data object with
	 * default values).
	 * @type {ManageDocumentsItemDataObject}
	 */
	item: null,
};

// Reducer function
const reducer = (state = {...initialState}, action) => {
	switch (action.type) {
		case REDUCER_TYPES.RESET: return { ...initialState };

		case REDUCER_TYPES.SET_LIST_DATA: return {...state, listData: action.listData};
		case REDUCER_TYPES.CLEAR_LIST_DATA: return {...state, listData: initialState.listData};

		case REDUCER_TYPES.SET_ITEM: return {...state, item: action.item};
		case REDUCER_TYPES.CLEAR_ITEM: return {...state, item: initialState.item};

		default: return state;
	}
};

// Selectors
export const selectors = {
	getManageDocumentsList: state => get(state, [reducerStoreKey, 'listData', 'data']),
	getManageDocumentsListPagination: state => ({
		perPage: getInteger(state, [reducerStoreKey, 'listData', 'perPage']),
		pageNo: getInteger(state, [reducerStoreKey, 'listData', 'pageNo']),
		totalRows: getInteger(state, [reducerStoreKey, 'listData', 'totalResults']),
		isLastPage: getBoolean(state, [reducerStoreKey, 'listData', 'isLastPage'])
	}),
	getManageDocumentsListSort: state => ({
		sortBy: getString(state, [reducerStoreKey, 'listData', 'sortBy']),
		sortDir: getString(state, [reducerStoreKey, 'listData', 'sortDir'], SORT_ORDER.ASC)
	}),
	getManageDocumentsListFilter: state => get(state, [reducerStoreKey, 'listData', 'filter'], null),

	getManageDocumentsItem: state => get(state, [reducerStoreKey, 'item']),
};

export default reducer;